<template>
  <div class="doc-browser">
    <!-- Loading overlay -->
    <div v-show="loading" class="position-relative" style="min-height: 200px;">
      <loading :active="loading" :is-full-page="false" color="blue" loader="spinner"></loading>
    </div>

    <div v-show="!loading && value.length > 0" class="doc-explorer">

      <!-- Filter chips -->
      <div class="doc-chips-bar">
        <button
          type="button"
          v-for="chip in availableTags"
          :key="chip.id"
          class="doc-chip"
          :class="{ active: activeTag === chip.id }"
          @click="activeTag = chip.id"
          data-track-category="DocFilter"
          data-track-action="select"
          :data-track-name="chip.id"
        >{{ chip.label }}</button>
      </div>

      <!-- Counter row + download selected -->
      <div class="doc-count-row">
        <div class="doc-count-left">
          {{ $tc('productDetails.documentBrowser.documentsCount', filteredDocuments.length, { count: filteredDocuments.length }) }}
        </div>
        <div class="doc-count-right" v-show="selectedIds.length > 0">
          <button type="button" class="doc-action-btn" @click="downloadSelected" :disabled="isDownloading || selectedIds.length > 10" :title="selectedIds.length > 10 ? $t('productDetails.documentBrowser.maxDownloadsExceeded') : ''">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {{ $tc('productDetails.documentBrowser.downloadSelected', selectedIds.length, { count: selectedIds.length }) }}
            <span v-if="isDownloading" class="doc-spinner"></span>
          </button>
        </div>
      </div>

      <!-- Select all -->
      <div class="doc-select-all-row" v-show="filteredDocuments.length > 0">
        <label class="doc-checkbox-label" :class="{ 'disabled': filteredDocuments.length > 10 }" :title="filteredDocuments.length > 10 ? $t('productDetails.documentBrowser.maxDownloadsExceeded') : ''">
          <input type="checkbox" :checked="selectAll" :disabled="filteredDocuments.length > 10" @change="toggleSelectAll($event.target.checked)" />
          <span>{{ $t('productDetails.documentBrowser.selectAll') }}</span>
        </label>
      </div>

      <!-- Document list -->
      <RecycleScroller
        class="doc-rows"
        :items="filteredDocuments"
        :item-size="75"
        key-field="id"
        :buffer="200"
        ref="scroller"
      >
        <template v-slot="{ item: doc }">
          <div class="doc-row-wrapper">
            <div
              class="doc-row"
              :class="{ selected: selectedIds.indexOf(doc.id) !== -1 }"
            >
              <!-- Checkbox -->
              <label class="doc-row-checkbox" @click.stop>
                <input type="checkbox" :value="doc.id" v-model="selectedIds" />
              </label>

              <!-- Clickable area to select the row -->
              <div class="doc-row-content" @click="toggleSelection(doc.id)">
                <div class="doc-row-main">
                  <span class="doc-row-name" :title="$t('productDetails.documentBrowser.clickToSelect')">{{ doc.filename }}</span>
                  <div class="doc-row-actions">
                    <span class="doc-tag" :data-tag="getTagLabel(doc.type)">{{ getTagLabel(doc.type) }}</span>
                    <!-- Pulsante Apri -->
                    <button type="button" class="doc-icon-btn" @click.stop="openDocument(doc)" :title="$t('productDetails.documentBrowser.openInNewTab')">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                    </button>
                    <!-- Pulsante Scarica -->
                    <button type="button" class="doc-icon-btn" @click.stop="downloadSingle(doc)" :title="$t('productDetails.documentBrowser.downloadDoc')">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                    </button>
                  </div>
                </div>
                <div class="doc-row-meta">{{ (doc.language || '').toUpperCase() }}</div>
              </div>
            </div>
          </div>
        </template>
      </RecycleScroller>

    </div>

    <!-- Empty state -->
    <div class="doc-empty" v-if="!loading && value.length === 0">
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" stroke-width="1.4">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
      </svg>
      <p>{{ $t('productDetails.documentBrowser.empty') }}</p>
    </div>

  </div>
</template>

<script>
import { downloadFile, base64ToBlob } from '../../utils/fileManager'
import { RecycleScroller } from 'vue-virtual-scroller'
import { track } from '@/plugins/tracking'

export default {
  name: 'Documentations',
  
  components: {
    RecycleScroller,
  },

  props: {
    value: {
      type: Array,
      default: function () { return [] },
    },
    isMop: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data: function () {
    return {
      activeTag: 'all',
      selectedIds: [],
      isDownloading: false,
      endpoint: '/api/detail/document/',
    }
  },

  computed: {
    availableTags: function () {
      var self = this
      var seen = {}
      var tags = []
      this.value.forEach(function (d) {
        var label = self.getTagLabel(d.type)
        if (!seen[label]) {
          seen[label] = true
          tags.push({ id: label, label: label })
        }
      })
      return [{ id: 'all', label: this.$t('productDetails.documentBrowser.allFilter') }].concat(tags)
    },

    filteredDocuments: function () {
      var self = this
      if (self.activeTag === 'all') return self.value
      return self.value.filter(function (d) {
        return self.getTagLabel(d.type) === self.activeTag
      })
    },

    selectAll: function () {
      return this.filteredDocuments.length > 0 && this.selectedIds.length === this.filteredDocuments.length
    },
  },

  watch: {
    activeTag: function () {
      // Deseleziona tutto quando si cambia filtro
      this.selectedIds = []
    },
    filteredDocuments: function () {
      // Reset dello scroll in alto quando cambiano i risultati filtrati
      if (this.$refs.scroller) {
        this.$refs.scroller.scrollToItem(0)
      }
    },
  },

  methods: {
    getTagLabel: function (type) {
      if (type == 1 && this.isMop) {
        return this.$t('productDetails.documentTypeLabel.isMop')
      }
      return this.$t('productDetails.documentTypeLabel.' + type)
    },

    toggleSelectAll: function (checked) {
      var self = this
      if (this.filteredDocuments.length > 10) return;

      if (checked) {
        this.selectedIds = this.filteredDocuments.map(function (d) { return d.id })
      } else {
        this.selectedIds = []
      }
    },

    downloadSingle: function (doc) {
      track.event('Document', 'download-single', doc && doc.id)
      try {
        this.isDownloading = true
        downloadFile(
          this,
          'get',
          this.endpoint + doc.id,
          false,
          'application/pdf;',
          'isDownloading',
          doc.filename,
          this.$t('search.label.exportResultsEmpty')
        )
      } catch (e) {
        console.error('Errore downloadSingle:', e)
        alert(this.$t('productDetails.documentBrowser.singleDownloadUnexpectedError') + (e.message || e))
        this.isDownloading = false
      }
    },

    toggleSelection: function (id) {
      var idx = this.selectedIds.indexOf(id)
      if (idx === -1) {
        this.selectedIds.push(id)
      } else {
        this.selectedIds.splice(idx, 1)
      }
    },

    openDocument: function (doc) {
      track.event('Document', 'open', doc && doc.id)
      var self = this
      self.isDownloading = true
      
      this.get(this.endpoint + doc.id)
        .then(function (resp) {
          if (!resp || !resp.base64ByteArray) {
            alert(self.$t('productDetails.documentBrowser.apiErrorNoDocument'))
            return
          }
          var blob = base64ToBlob(resp.base64ByteArray)
          var objectUrl = window.URL.createObjectURL(blob)
          
          window.open(objectUrl, '_blank')
        })
        .catch(function (err) {
          console.error('API Error per ' + doc.filename, err)
          var msg = err.message || JSON.stringify(err)
          if (err.response && err.response.status) {
            msg = 'HTTP ' + err.response.status + ' - ' + err.response.statusText
          }
          alert(self.$t('productDetails.documentBrowser.openFileError') + '\n' + msg)
        })
        .finally(function () {
          self.isDownloading = false
        })
    },

    downloadSelected: function () {
      var self = this

      var selected = self.value.filter(function (d) {
        return self.selectedIds.indexOf(d.id) !== -1
      })

      // Controllo di sicurezza lato frontend.
      // Nessun messaggio di errore esplicito come richiesto: blocca l'esecuzione silenziosamente
      // se l'utente dovesse riuscire a bypassare il tasto disabled (es. tramite console).
      if (selected.length > 10) {
        return
      }

      self.isDownloading = true
      track.event('Document', 'download-selected', selected.length)

      // Scarica ogni file in sequenza per evitare di sovraccaricare il backend (HTTP 500)
      var sequence = Promise.resolve()
      var errorLogs = []

      selected.forEach(function (doc) {
        sequence = sequence.then(function () {
          return self.get(self.endpoint + doc.id)
            .then(function (resp) {
              if (!resp || !resp.base64ByteArray) {
                 errorLogs.push(doc.filename + ': ' + self.$t('productDetails.documentBrowser.apiErrorNoDocument'))
              } else {
                  // Triggera il download immediato del singolo PDF
                  var blob = base64ToBlob(resp.base64ByteArray)
                  var link = document.createElement('a')
                  link.href = window.URL.createObjectURL(blob)
                  link.download = resp.filename || doc.filename
                  document.body.appendChild(link)
                  link.click()
                  document.body.removeChild(link)
                  window.URL.revokeObjectURL(link.href)
              }
            })
            .catch(function (err) {
              console.error('API Error per ' + doc.filename, err)
              var msg = err.message || JSON.stringify(err)
              if (err.response && err.response.status) {
                msg = 'HTTP ' + err.response.status + ' - ' + err.response.statusText
              }
              errorLogs.push(doc.filename + ': ' + msg)
            })
        })
      })

      sequence
        .then(function () {
          if (errorLogs.length > 0) {
            alert(self.$t('productDetails.documentBrowser.someFilesNotDownloaded') + '\n' + errorLogs.join('\n'))
          }
          self.isDownloading = false
        })
        .catch(function (e) {
          alert(self.$t('productDetails.documentBrowser.unexpectedError') + '\n' + (e.message || e))
          self.isDownloading = false
        })
    },
  },
}
</script>

<style>
@import '~vue-virtual-scroller/dist/vue-virtual-scroller.css';
</style>

<style scoped>
/* ── BROWSER CONTAINER ── */
.doc-browser {
  width: 100%;
  overflow: hidden;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 13px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  margin-top: 12px;
  margin-bottom: 12px;
}

.doc-explorer {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* ── CHIP BAR ── */
.doc-chips-bar {
  display: flex;
  gap: 6px;
  padding: 12px 14px 10px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.doc-chip {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.1s, color 0.1s, border-color 0.1s;
}
.doc-chip:hover {
  background: #e2e8f0;
  border-color: #cbd5e1;
  color: #374151;
}
.doc-chip.active {
  background: #e5e7eb;
  border-color: #d1d5db;
  color: #374151;
  font-weight: 600;
}

/* ── COUNT ROW ── */
.doc-count-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11.5px;
  color: #9ca3af;
  padding: 8px 14px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}
.doc-count-left {
  flex: 1;
}
.doc-count-right {
  flex-shrink: 0;
}

/* ── SELECT ALL ── */
.doc-select-all-row {
  padding: 6px 14px 6px 28px; /* Allineato col checkbox delle righe (14px di parent + 14px di row) */
  border-bottom: 1px solid #f1f5f9;
}
.doc-checkbox-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 11.5px;
  color: #6b7280;
  cursor: pointer;
  margin: 0;
}
.doc-checkbox-label input[type="checkbox"] {
  accent-color: #E05A2B;
  width: 14px;
  height: 14px;
  cursor: pointer;
}
.doc-checkbox-label.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.doc-checkbox-label.disabled input[type="checkbox"] {
  cursor: not-allowed;
}

/* ── DOCUMENT LIST ── */
.doc-rows {
  flex: 1;
  overflow-y: auto;
  max-height: 450px;
  padding: 8px 14px 16px 14px; /* Aggiunto padding attorno alle schede per staccarle dai bordi */
}

.doc-row-wrapper {
  padding-bottom: 5px;
  height: 100%;
  box-sizing: border-box;
}

.doc-row {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  cursor: pointer;
  border: 1px solid #f3f4f6;
  border-radius: 8px;
  background: #ffffff;
  transition: all 0.2s ease;
  gap: 12px;
  height: 100%;
  box-sizing: border-box;
}
.doc-row:hover {
  border-color: #e5e7eb;
  background: #fcfcfc;
  box-shadow: 0 2px 4px rgba(0,0,0,0.02);
}
.doc-row.selected {
  background: linear-gradient(135deg, #a78bfa12, #f472b60a, #60a5fa0a);
  border: 1px solid #a78bfa73;
  box-shadow: 0 0 0 1px #a78bfa73;
}

.doc-row-checkbox {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
  margin: 0;
}
.doc-row-checkbox input[type="checkbox"] {
  accent-color: #E05A2B;
  width: 15px;
  height: 15px;
  cursor: pointer;
}

.doc-row-content {
  flex: 1;
  min-width: 0;
}
.doc-row-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 2px;
}
.doc-row-name {
  font-size: 13.5px;
  font-weight: 500;
  color: #374151; /* Testo scuro normale */
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.doc-row-actions {
  display: flex;
  align-items: center;
  gap: 8px; /* Migliorato lo spacing tra tag e icone */
}

.doc-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%; /* Tasti circolari per UI moderna */
  background: transparent;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  padding: 0;
}
.doc-icon-btn:hover {
  background: #f3f4f6;
  color: #0284c7; /* Diventano blu e si evidenziano all'hover */
}

.doc-row.selected .doc-row-name,
.doc-row.selected .doc-row-meta,
.doc-row.selected .doc-icon-btn {
  color: #0284c7; /* Scritte interne e icone azzurre quando selezionato */
}
.doc-row.selected .doc-icon-btn:hover {
  background: #e0f2fe;
}
.doc-row-meta {
  font-size: 11.5px;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── TAG BADGES ── */
.doc-tag {
  display: inline-block;
  font-size: 10.5px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 12px;
  flex-shrink: 0;
  white-space: nowrap;
}
.doc-tag[data-tag="KID"]           { background: #dcfce7; color: #16a34a; } /* Verde */
.doc-tag[data-tag="GKID"]          { background: #dcfce7; color: #16a34a; }
.doc-tag[data-tag="GKID + SIDs"]   { background: #dcfce7; color: #16a34a; }
.doc-tag[data-tag="DIP"]           { background: #e0f2fe; color: #0284c7; } /* Blu chiaro */
.doc-tag[data-tag="SFDR"]          { background: #f0fdf4; color: #16a34a; }
.doc-tag[data-tag="CA"]            { background: #f3f4f6; color: #4b5563; } /* Grigio */
.doc-tag[data-tag="SID (separati)"]{ background: #fdf4ff; color: #a855f7; }
.doc-tag[data-tag="SID (splitted)"]{ background: #fdf4ff; color: #a855f7; }
.doc-tag[data-tag="Allegati"]      { background: #fef3c7; color: #b45309; }
.doc-tag[data-tag="Attachments"]   { background: #fef3c7; color: #b45309; }
.doc-tag[data-tag="Sostenibilità"] { background: #f0fdf4; color: #16a34a; }
.doc-tag[data-tag="Sustainability"]{ background: #f0fdf4; color: #16a34a; }

/* ── ACTION BUTTON ── */
.doc-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
  background: #ffffff;
  color: #6b7280; /* Grigio coerente con l'icona SVG */
  border: 1px solid #d1d5db;
  transition: background 0.12s, border-color 0.12s;
}
.doc-action-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}
.doc-action-btn:disabled {
  opacity: 0.4;
  cursor: default; /* Impostato su default per evitare icone di blocco o caricamento errate */
  background: #f9fafb;
  color: #9ca3af;
}

/* ── SPINNER ── */
.doc-spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(2, 132, 199, 0.3);
  border-top-color: #0284c7;
  border-radius: 50%;
  animation: doc-spin 0.6s linear infinite;
}
@keyframes doc-spin {
  to { transform: rotate(360deg); }
}

/* ── EMPTY STATE ── */
.doc-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #9ca3af;
  font-size: 13.5px;
  padding: 60px 40px;
}
.doc-empty p {
  margin: 0;
}
</style>
